import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ElliottFace from '../../../../components/ElliottFace';
import HeaderElliottSlot from '../../../../../../components/layout/HeaderElliottSlot';
import { useIsMobileApp } from '../../../../../../mobile/mobileApp';

interface Props {
  // Opens the Elliott chat modal in "menu" mode (Elliott offers the actions).
  onOpen: () => void;
  orgName?: string;
}

// Elliott orb at the top-right of the Learning Framework — pulsates and "speaks"
// a rotating set of typing prompts. Clicking it opens the chat modal where
// Elliott offers the framework actions (Identify Knowledge Gaps & Risks /
// Sync Knowledge / Add New Program / Invite Admin / Edit Framework) as a
// starting point.
const FrameworkElliottMenu: React.FC<Props> = ({ onOpen, orgName }) => {
  // Each line types itself out, holds, then is replaced by the next one.
  const SPEECHES = useMemo(
    () => [
      // The gaps line was dropped when Gaps & Risks moved onto the dashboard
      // itself — the orb no longer offers a way in, so it shouldn't advertise one.
      'Spin up a new training for your team',
      `Elliott here, I'm ready to power ${orgName?.trim() || 'your organization'}'s learning.`
    ],
    [orgName]
  );

  const [index, setIndex] = useState(0);
  const [count, setCount] = useState(0);
  const speech = SPEECHES[index];
  // A phone shows the orb without its bubble (mobile-app.css), so nothing is typed there.
  const isMobileApp = useIsMobileApp();

  useEffect(() => {
    if (isMobileApp) return undefined;
    setCount(0);
    let hold: number | undefined;
    let typed = 0;
    const type = window.setInterval(() => {
      typed += 1;
      setCount(typed);
      if (typed >= speech.length) {
        window.clearInterval(type);
        // Leave the finished line up long enough to read before the next one.
        hold = window.setTimeout(() => setIndex((i) => (i + 1) % SPEECHES.length), 3500);
      }
    }, 45);
    return () => {
      window.clearInterval(type);
      if (hold) window.clearTimeout(hold);
    };
  }, [speech, SPEECHES.length, isMobileApp]);

  const typed = speech.slice(0, count);
  const typing = count < speech.length;

  return (
    <HeaderElliottSlot>
    <Box
      onClick={onOpen}
      // Lives in the top bar, left of the notifications bell (via
      // HeaderElliottSlot). The bubble is capped so it never crowds the header.
      sx={{
        maxWidth: 520,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        cursor: 'pointer',
        minWidth: 0
      }}
    >
      {/* Speech bubble (left of the orb). A phone shows the orb alone (mobile-app.css). */}
      <Box
        className="elliott-orb-bubble"
        sx={{
          position: 'relative',
          minWidth: 0,
          overflow: 'hidden',
          px: '12px',
          py: '8px',
          borderRadius: '10px',
          bgcolor: '#1C1C1C',
          border: '1.5px solid #F79420',
          boxShadow: '0 0 10px rgba(247,148,32,0.25)',
          // Little tail pointing at the orb (brandy edge to match the outline).
          '&::after': {
            content: '""',
            position: 'absolute',
            right: -7,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 0,
            height: 0,
            borderTop: '6px solid transparent',
            borderBottom: '6px solid transparent',
            borderLeft: '7px solid #F79420'
          }
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 13,
            fontWeight: 500,
            fontStyle: 'italic',
            lineHeight: 1.4,
            color: '#FDFDFD',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {typed}
          {typing && (
            <Box
              component="span"
              sx={{
                // Zero-width caret: it never adds to the bubble's width, so when
                // typing finishes and the caret is removed the bubble doesn't
                // shrink — the finished text stays put instead of jumping back.
                // (Previously the caret was a normal inline glyph, so removing it
                // shrank the bubble by roughly its width.) width:0 lets the glyph
                // overflow just past the last character without affecting layout.
                display: 'inline-block',
                width: 0,
                transform: 'translateX(1px)',
                color: '#F79420',
                animation: 'elliottOrbCaret 1s steps(1) infinite',
                '@keyframes elliottOrbCaret': { '0%,50%': { opacity: 1 }, '50.01%,100%': { opacity: 0 } }
              }}
            >
              ▍
            </Box>
          )}
        </Typography>
      </Box>

      {/* Pulsating orb with a living, smiling face — the interactive part, so
          it keeps its size however tight the header gets. */}
      <Box sx={{ flexShrink: 0, display: 'flex' }}>
        <ElliottFace size={34} pulse />
      </Box>
    </Box>
    </HeaderElliottSlot>
  );
};

export default FrameworkElliottMenu;
